import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import { cn } from "@/modules/lib/utils"
import { ScoreType } from "@/modules/scores/scoreTypes"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import CongratulationImage from "@images/scoreboard/congratulations.svg"
import FeedbacksImage from "@images/scoreboard/feedbacks.svg"
import { word } from "@modules/core/utils/i18n"
import { ArrowRightIcon, LockKeyholeIcon } from "lucide-react"
import { useMemo } from "react"

type Props = {
  session: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity | BoxSessionDetailsEntity
  scoreboardLocked?: boolean
  onClick: () => void
  canUserAccessToScoreboard: boolean
  isOwner: boolean
  isAuthor: boolean
  isAcceptedSharedCoach: boolean
} & React.ComponentProps<"div">

export function ScoreboardCard({
  onClick,
  session,
  scoreboardLocked,
  canUserAccessToScoreboard,
  isOwner,
  isAuthor,
  isAcceptedSharedCoach,
  className,
  ...props
}: Props) {
  const sessionOnlyHasFeedbackRequested = useMemo(() => {
    return session.blocks.every((block) => {
      if (block.type !== SessionBlockType.SESSION_EXERCISE) return true
      if (block.content.scoreSetConfigurations.length === 0) return true
      if (
        [ScoreType.TEXT_FROM_ATHLETE, ScoreType.NO_SCORE].includes(
          block.content.scoreSetConfigurations[0]?.scoreType ?? ScoreType.NO_SCORE,
        )
      )
        return true

      return false
    })
  }, [session])

  const isScoreboardLocked = useMemo(() => {
    return scoreboardLocked && !isAuthor && !isAcceptedSharedCoach && !canUserAccessToScoreboard
  }, [scoreboardLocked, isAuthor, isAcceptedSharedCoach, canUserAccessToScoreboard])

  const cardTitle = useMemo(() => {
    if (sessionOnlyHasFeedbackRequested) {
      return word("session.scoreboard.card.feedback.title")
    }
    return word("session.scoreboard.card.title")
  }, [session, sessionOnlyHasFeedbackRequested])

  const cardDescription = useMemo(() => {
    if (isOwner) return word("session.scoreboard.card.descriptionOwner")
    else if (isScoreboardLocked) {
      if (sessionOnlyHasFeedbackRequested) return word("session.scoreboard.card.feedback.descriptionLocked")
      else return word("session.scoreboard.card.descriptionLock")
    } else {
      if (sessionOnlyHasFeedbackRequested) return word("session.scoreboard.card.feedback.description")
      else return word("session.scoreboard.card.description")
    }
  }, [
    session,
    sessionOnlyHasFeedbackRequested,
    isOwner,
    scoreboardLocked,
    isAuthor,
    isAcceptedSharedCoach,
    canUserAccessToScoreboard,
  ])

  return (
    <div
      className={cn(
        "relative flex w-full flex-row justify-between overflow-hidden rounded-3xl p-4.5",
        { "shiny-hover cursor-pointer bg-linear-to-r from-primary-600 to-primary-500": !isScoreboardLocked },
        { "bg-neutral-50": isScoreboardLocked },
        className,
      )}
      onClick={onClick}
      {...props}
    >
      <div className="flex flex-col items-start justify-between">
        <Text
          className={cn("text-textDefault-heading", { "text-textOnSurface-disabled": isScoreboardLocked })}
          fontStyle="TS"
        >
          {cardTitle}
        </Text>
        <Text
          className={cn("text-textDefault-body", { "text-textOnSurface-disabled": isScoreboardLocked })}
          fontStyle="LS"
        >
          {cardDescription}
        </Text>
      </div>
      <div className="flex flex-col items-end">
        <div>
          {isScoreboardLocked ? (
            <Button variant="secondarySecondary" size="iconLg" className="" disabled>
              <LockKeyholeIcon />
            </Button>
          ) : (
            <Button variant="secondarySecondary" size="iconLg" className="">
              <ArrowRightIcon />
            </Button>
          )}
        </div>
      </div>
      <Decoration isFeedback={sessionOnlyHasFeedbackRequested} isLocked={isScoreboardLocked ?? false} />
    </div>
  )
}

const Decoration = ({ isFeedback, isLocked }: { isFeedback: boolean; isLocked: boolean }) => {
  if (isFeedback)
    return (
      <img
        className={cn("absolute right-[70px] bottom-[-10px] z-0 h-26 w-36", { "opacity-20": isLocked })}
        src={FeedbacksImage}
        alt="Decoration"
      />
    )
  else
    return (
      <img
        className={cn("absolute right-[50px] bottom-[-45px] z-0 h-43 w-59", { "opacity-20": isLocked })}
        src={CongratulationImage}
        alt="Decoration"
      />
    )
}
